/**
 * ESC/POS Command Utilities for Thermal Printers
 * Supports 80mm thermal printers like XP-80C
 */

class ESCPOSPrinter {
  constructor() {
    // ESC/POS control characters
    this.ESC = '\x1B'
    this.GS = '\x1D'
    this.NUL = '\x00'
    this.LF = '\x0A'
    this.CR = '\x0D'
    this.DLE = '\x10'
    this.DC4 = '\x14'
    this.CAN = '\x18'
    this.FF = '\x0C'

    // Paper width for 80mm (characters per line)
    this.PAPER_WIDTH = 48 // Standard for 80mm thermal paper
  }

  /**
   * Initialize printer - reset to default settings
   */
  init() {
    return this.ESC + '@'
  }

  /**
   * Font selection and formatting
   */
  font(type = 'A') {
    // Font A: 12x24 dots (standard)
    // Font B: 9x17 dots (condensed)
    if (type === 'B') {
      return this.ESC + '!' + '\x01'
    }
    return this.ESC + '!' + '\x00' // Font A (default)
  }

  /**
   * Set text emphasis (bold)
   */
  bold(enable = true) {
    return this.ESC + 'E' + (enable ? '\x01' : '\x00')
  }

  /**
   * Set text underline
   */
  underline(mode = 0) {
    // 0: off, 1: 1-dot thick, 2: 2-dot thick
    return this.ESC + '-' + String.fromCharCode(mode)
  }

  /**
   * Set text alignment
   */
  align(position = 'left') {
    const alignments = {
      'left': '\x00',
      'center': '\x01',
      'right': '\x02'
    }
    return this.ESC + 'a' + (alignments[position] || alignments.left)
  }

  /**
   * Set print density (darkness)
   */
  density(level = 8) {
    // Level 0-15 (8 is default, 15 is darkest)
    const densityLevel = Math.max(0, Math.min(15, level))
    return this.GS + '(' + 'K' + '\x02' + '\x00' + '0' + String.fromCharCode(densityLevel)
  }

  /**
   * Set character spacing
   */
  characterSpacing(spacing = 0) {
    // spacing: 0-255 (0 = default)
    return this.ESC + ' ' + String.fromCharCode(spacing)
  }

  /**
   * Set line spacing
   */
  lineSpacing(spacing = null) {
    if (spacing === null) {
      // Reset to default line spacing
      return this.ESC + '2'
    }
    // Set line spacing to n/180 inch
    return this.ESC + '3' + String.fromCharCode(spacing)
  }

  /**
   * Text size (width and height multiplier)
   */
  textSize(width = 1, height = 1) {
    // width: 1-8, height: 1-8
    const w = Math.max(1, Math.min(8, width)) - 1
    const h = Math.max(1, Math.min(8, height)) - 1
    const size = (w << 4) | h
    return this.GS + '!' + String.fromCharCode(size)
  }

  /**
   * Cut paper (full or partial)
   */
  cut(type = 'full') {
    if (type === 'partial') {
      return this.GS + 'V' + '\x01' // Partial cut
    }
    return this.GS + 'V' + '\x00' // Full cut
  }

  /**
   * Print and feed paper
   */
  feed(lines = 1) {
    return this.ESC + 'd' + String.fromCharCode(lines)
  }

  /**
   * Print horizontal line separator
   */
  separator(char = '-', width = null) {
    const lineWidth = width || this.PAPER_WIDTH
    return char.repeat(lineWidth) + this.LF
  }

  /**
   * Print dashed line separator
   */
  dashedSeparator(width = null) {
    return this.separator('-', width)
  }

  /**
   * Print solid line separator
   */
  solidSeparator(width = null) {
    return this.separator('=', width)
  }

  /**
   * Format text with padding for receipts
   */
  formatLine(left, right = '', totalWidth = null) {
    const width = totalWidth || this.PAPER_WIDTH
    const leftText = String(left).substring(0, width - 1)
    const rightText = String(right).substring(0, width - leftText.length)
    const padding = ' '.repeat(Math.max(0, width - leftText.length - rightText.length))

    return leftText + padding + rightText + this.LF
  }

  /**
   * Center text within paper width
   */
  centerText(text, width = null) {
    const lineWidth = width || this.PAPER_WIDTH
    const textLength = String(text).length

    if (textLength >= lineWidth) {
      return text + this.LF
    }

    const padding = Math.floor((lineWidth - textLength) / 2)
    return ' '.repeat(padding) + text + this.LF
  }

  /**
   * Print QR Code
   */
  qrCode(data, size = 3) {
    let commands = ''

    // Set QR code model
    commands += this.GS + '(k' + '\x04' + '\x00' + '1A' + '2' + '\x00'

    // Set QR code size
    commands += this.GS + '(k' + '\x03' + '\x00' + '1C' + String.fromCharCode(size)

    // Set QR code error correction level
    commands += this.GS + '(k' + '\x03' + '\x00' + '1E' + '0' // Level L

    // Store QR code data
    const dataLength = data.length + 3
    const pL = String.fromCharCode(dataLength & 0xFF)
    const pH = String.fromCharCode((dataLength >> 8) & 0xFF)
    commands += this.GS + '(k' + pL + pH + '1P0' + data

    // Print QR code
    commands += this.GS + '(k' + '\x03' + '\x00' + '1Q' + '0'

    return commands
  }

  /**
   * Print barcode (Code128)
   */
  barcode(data, type = 'CODE128', height = 60) {
    let commands = ''

    // Set barcode height
    commands += this.GS + 'h' + String.fromCharCode(height)

    // Set barcode width
    commands += this.GS + 'w' + '\x02' // Width 2

    // Print barcode
    commands += this.GS + 'k' + '\x73' + String.fromCharCode(data.length) + data

    return commands
  }

  /**
   * Open cash drawer
   */
  openDrawer() {
    return this.ESC + 'p' + '\x00' + '\x19' + '\x19'
  }

  /**
   * Complete receipt template for Dekacare
   */
  buildReceipt(data) {
    let receipt = ''

    // Initialize printer
    receipt += this.init()
    receipt += this.density(15) // Max density for best quality
    receipt += this.font('A') // Use standard font

    // Header with logo placeholder and facility info
    receipt += this.align('center')
    receipt += this.bold(true)
    receipt += this.textSize(1, 2)
    receipt += (data.entityName || 'DEKACARE') + this.LF
    receipt += this.textSize(1, 1)
    receipt += this.bold(false)

    if (data.entityAddress) {
      receipt += data.entityAddress + this.LF
    }
    if (data.entityPhone) {
      receipt += data.entityPhone + this.LF
    }

    receipt += this.solidSeparator()

    // Patient info
    receipt += this.align('left')
    receipt += this.formatLine('Waktu:', new Date().toLocaleString('id-ID'))
    if (data.norm) {
      receipt += this.formatLine('No. RM:', data.norm)
    }
    if (data.patientName) {
      receipt += this.formatLine('Nama:', data.patientName)
    }
    if (data.birthDate) {
      receipt += this.formatLine('Tgl. Lahir:', data.birthDate)
    }

    receipt += this.dashedSeparator()

    // Services header
    receipt += this.align('center')
    receipt += this.bold(true)
    receipt += 'LAYANAN' + this.LF
    receipt += this.bold(false)
    receipt += this.dashedSeparator()

    // Services list
    receipt += this.align('left')
    if (data.services && Array.isArray(data.services)) {
      data.services.forEach(service => {
        receipt += this.formatLine(
          service.name || service.key,
          this.formatMoney(service.total || service.amount || 0)
        )
      })
    }

    receipt += this.solidSeparator()

    // Total
    receipt += this.bold(true)
    receipt += this.formatLine('Grand Total:', this.formatMoney(data.total || 0))
    receipt += this.bold(false)

    receipt += this.dashedSeparator()

    // Footer
    if (data.doctor) {
      receipt += this.align('center')
      receipt += new Date().toLocaleDateString('id-ID') + this.LF
      receipt += this.LF
      receipt += data.doctor + this.LF
    }

    receipt += this.align('center')
    receipt += this.LF
    receipt += 'Powered By: dekate (www.dekate.id)' + this.LF

    // QR Code if data provided
    if (data.qrData) {
      receipt += this.LF
      receipt += this.qrCode(data.qrData, 3)
      receipt += this.LF
    }

    // Cut paper
    receipt += this.feed(3)
    receipt += this.cut('full')

    return receipt
  }

  /**
   * Format money for Indonesian currency
   */
  formatMoney(amount) {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0)
  }

  /**
   * Format money without currency symbol (for better alignment)
   */
  formatMoneySimple(amount) {
    return new Intl.NumberFormat('id-ID', {
      minimumFractionDigits: 0
    }).format(amount || 0)
  }

  /**
   * Build E-Resep receipt specifically for StrukEResepPos component
   * This matches the format from StrukEResepPos.vue
   */
  buildEResepReceipt(data) {
    let receipt = ''

    // Initialize printer
    receipt += this.init()
    receipt += this.density(15) // Max density for best quality
    receipt += this.font('A') // Use standard font

    // HEADER SECTION
    receipt += this.align('center')

    // Entity name (bold, larger)
    receipt += this.bold(true)
    receipt += this.textSize(1, 2)
    receipt += (data.entityData?.name || 'DEKACARE') + this.LF
    receipt += this.textSize(1, 1)
    receipt += this.bold(false)

    // Address and phone
    if (data.entityData?.address) {
      receipt += data.entityData.address + this.LF
    }
    if (data.entityData?.phone) {
      receipt += data.entityData.phone + this.LF
    }

    receipt += this.solidSeparator()

    // TITLE SECTION
    receipt += this.align('center')
    receipt += this.bold(true)
    if (data.isInvoice && data.noNota) {
      receipt += 'No.Nota: ' + data.noNota + this.LF
    } else {
      receipt += 'Resep Rawat Jalan' + this.LF
    }
    receipt += this.bold(false)

    // Date and location info
    if (data.kunjungan?.ruangan?.deskripsi) {
      receipt += data.kunjungan.ruangan.deskripsi + ' '
    }
    receipt += new Date().toLocaleString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }) + this.LF

    receipt += this.dashedSeparator()

    // PATIENT INFO SECTION
    receipt += this.align('left')
    if (data.kunjungan?.pendaftaran?.pasien?.nama) {
      const patient = data.kunjungan.pendaftaran.pasien
      const norm = data.kunjungan.pendaftaran.norm
      const birthDate = this.formatDate(patient.tanggal_lahir)
      receipt += patient.nama
      if (norm) {
        receipt += ' (No.RM ' + norm + ')'
      }
      if (birthDate) {
        receipt += ' - ' + birthDate
      }
      receipt += this.LF
    } else if (data.pasien) {
      receipt += data.pasien.nama
      if (data.pasien.no_rm && data.pasien.tglLahir) {
        receipt += ' (No.RM ' + data.pasien.no_rm + ') - ' + this.formatDate(data.pasien.tglLahir)
      }
      receipt += this.LF
    } else if (data.isInvoice && data.summary?.nama_pembeli) {
      receipt += data.summary.nama_pembeli + this.LF
    } else {
      receipt += 'Pembeli Langsung.' + this.LF
    }

    receipt += this.dashedSeparator()

    // MEDICAL HISTORY SECTION (only for non-invoice)
    if (!data.isInvoice) {
      receipt += 'Riwayat' + this.LF
      receipt += this.formatLine(
        '[' + this.xCheck(data.statusGinjal) + '] Gangguan Fungsi Ginjal',
        'Hamil [' + this.xCheck(data.statusHamil) + ']'
      )
      receipt += this.formatLine(
        '[' + this.xCheck(data.statusMenyusui) + '] Menyusui',
        'Cito [' + this.xCheck(data.statusCito) + ']'
      )
      receipt += this.solidSeparator()
    }

    // NON-RACIK MEDICINES SECTION
    if (data.listObatNonRacik && data.listObatNonRacik.length > 0) {
      if (data.listObatRacik && data.listObatRacik.length > 0) {
        receipt += 'Daftar Obat Non Racik' + this.LF
      }

      data.listObatNonRacik.forEach(item => {
        // Medicine name (bold)
        receipt += this.bold(true)
        receipt += item.namaObat + this.LF
        receipt += this.bold(false)

        // Usage instructions
        if (item.aturanMinum) {
          receipt += item.aturanMinum + this.LF
        }

        // Price and quantity
        receipt += this.formatLine(
          '@' + this.formatMoneySimple(item.hargaSatuan) + ' x ' + item.jumlah,
          this.formatMoneySimple(item.hasilJumlahHarga)
        )
      })

      receipt += this.dashedSeparator()
      receipt += this.bold(true)
      receipt += this.formatLine('Sub Total', this.formatMoneySimple(data.grandTotalObatNonRacik || 0))
      receipt += this.bold(false)
    }

    // RACIK MEDICINES SECTION
    if (data.listObatRacik && data.listObatRacik.length > 0) {
      if (data.listObatNonRacik && data.listObatNonRacik.length > 0) {
        receipt += this.dashedSeparator()
        receipt += 'Daftar Obat Racik' + this.LF
      }

      data.listObatRacik.forEach(item => {
        receipt += item.namaRacik + this.LF
        receipt += this.formatLine(
          '@' + this.formatMoneySimple(item.hasilJumlahHarga / item.jumlah) + ' x ' + item.jumlah,
          this.formatMoneySimple(item.hasilJumlahHarga)
        )
      })

      if (data.listObatNonRacik && data.listObatNonRacik.length > 0) {
        receipt += this.dashedSeparator()
        receipt += this.bold(true)
        receipt += this.formatLine('Sub Total', this.formatMoneySimple(data.grandTotalObatRacik || 0))
        receipt += this.bold(false)
      }
    }

    // TOTAL SECTION
    if (!data.isInvoice && data.listObatNonRacik && data.listObatNonRacik.length > 0) {
      receipt += this.dashedSeparator()
      receipt += this.bold(true)
      receipt += this.formatLine('Total', this.formatMoneySimple(data.subTotal || 0))
      receipt += this.bold(false)
    }

    // INVOICE SPECIFIC ADJUSTMENTS
    if (data.isInvoice && data.summary) {
      if (data.summary.ongkos_kirim && parseInt(data.summary.ongkos_kirim) > 0) {
        receipt += this.bold(true)
        receipt += this.formatLine('Ongkos Kirim', this.formatMoneySimple(data.summary.ongkos_kirim))
        receipt += this.bold(false)
      }
      if (data.summary.biaya_layanan && parseInt(data.summary.biaya_layanan) > 0) {
        receipt += this.bold(true)
        receipt += this.formatLine('Biaya Layanan', this.formatMoneySimple(data.summary.biaya_layanan))
        receipt += this.bold(false)
      }
      if (data.summary.diskon_total && parseInt(data.summary.diskon_total) > 0) {
        receipt += this.bold(true)
        receipt += this.formatLine('Diskon', this.formatMoneySimple(data.summary.diskon_total))
        receipt += this.bold(false)
      }
      if (data.summary.pembulatan && parseInt(data.summary.pembulatan) !== 0) {
        receipt += this.bold(true)
        receipt += this.formatLine('Pembulatan', this.formatMoneySimple(data.summary.pembulatan))
        receipt += this.bold(false)
      }
    }

    // GRAND TOTAL
    receipt += this.solidSeparator()
    receipt += this.bold(true)
    receipt += this.textSize(1, 2)
    receipt += this.formatLine('Grand Total', this.formatMoneySimple(data.grandTotalWithAdjustments || 0))
    receipt += this.textSize(1, 1)
    receipt += this.bold(false)

    // PAYMENT INFO (for invoice)
    if (data.isInvoice && data.summary) {
      if (data.summary.nominal_bayar && data.summary.payment_method) {
        receipt += this.formatLine(
          'Dibayar: ' + data.summary.payment_method,
          this.formatMoneySimple(data.summary.nominal_bayar)
        )
      }
      if (data.summary.nominal_bayar && data.kembalian !== undefined) {
        receipt += this.formatLine('Kembalian', this.formatMoneySimple(data.kembalian))
      }
    }

    // SIGNATURE SECTION (only for non-invoice)
    if (!data.isInvoice) {
      receipt += this.dashedSeparator()
      receipt += this.align('center')
      receipt += this.formatLine('Petugas', 'Penerima', 32)
      receipt += this.LF + this.LF + this.LF

      if (data.userData?.name_with_title) {
        receipt += this.align('left')
        receipt += this.formatLine('(' + data.userData.name_with_title + ')', '(          )', 32)
      } else {
        receipt += this.formatLine('(          )', '(          )', 32)
      }
    }

    // FOOTER SECTION
    if (data.footerText) {
      receipt += this.solidSeparator()
      receipt += this.align('center')
      receipt += data.footerText + this.LF
    }

    receipt += this.solidSeparator()
    receipt += this.align('center')
    receipt += 'Powered By: dekate (www.dekate.id)' + this.LF
    receipt += 'Str e-POS' + this.LF

    // QR Code if provided
    if (data.qrContent) {
      receipt += this.LF
      receipt += this.qrCode(data.qrContent, 3)
      receipt += this.LF
    }

    // Cut paper
    receipt += this.feed(3)
    receipt += this.cut('full')

    return receipt
  }

  /**
   * Helper function to format date
   */
  formatDate(date) {
    if (!date) return ''

    // Handle DD-MM-YYYY format
    if (typeof date === 'string' && date.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const parts = date.split('-')
      const dateObj = new Date(parts[2], parts[1] - 1, parts[0])
      return dateObj.toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    }

    // Handle other date formats
    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) return ''

    return dateObj.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  /**
   * Helper function for checkboxes
   */
  xCheck(value) {
    return value ? '✓' : ' '
  }
}

export default ESCPOSPrinter